// ✅ SupplierMainMasListing.jsx
// ✅ New standardized listing design
// ✅ Existing APIs preserved
// ✅ Search preserved
// ✅ Delete with ConfirmModal preserved
// ✅ Add New preserved
// ✅ Edit preserved
// ✅ Pagination preserved
// ✅ Dark mode + skin preserved

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import ConfirmModal from "../../components/ConfirmModal";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  ToastContainer,
  toast,
} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaEdit,
  FaSearch,
  FaPlus,
  FaTrashAlt,
  FaTimes,
  FaUsers,
  FaFilter,
  FaArrowLeft,
  FaCog,
  FaCheckSquare,
  FaUserTie,
} from "react-icons/fa";

/* =========================================================
   API BASE
========================================================= */

const API_BASE =
  "https://nissi-india.com/api";

/* =========================================================
   HELPERS
========================================================= */

const toStr = (value) =>
  String(value ?? "").trim();

/* =========================================================
   COMPONENT
========================================================= */

const SupplierMainMasListing =
  () => {
    /* =======================================================
       THEME
    ======================================================= */

    const {
      isDark,
      skin,
      setSkin,
      theme,
    } = useContext(
      ThemeContext
    );

    const currentSkin =
      useMemo(
        () =>
          getCurrentSkin(
            theme || skin
          ),
        [theme, skin]
      );

    const {
      baseColor = "blue",
      baseShade = "600",
    } =
      currentSkin || {};

    /* =======================================================
       ROUTER
    ======================================================= */

    const location =
      useLocation();

    const navigate =
      useNavigate();

    /* =======================================================
       LOCAL STORAGE
    ======================================================= */

    const finYearId =
      localStorage.getItem(
        "finYearId"
      ) || "";

    const finYear =
      localStorage.getItem(
        "finYear"
      ) || "";

    const branchId =
      localStorage.getItem(
        "branchId"
      ) || "";

    const branch =
      localStorage.getItem(
        "branchType"
      ) || "";

    const userId =
      localStorage.getItem(
        "userId"
      ) || "";

    /* =======================================================
       HEADERS
    ======================================================= */

    const commonHeaders =
      useMemo(
        () => ({
          "x-finyearid":
            finYearId,

          "x-finyear":
            finYear,

          "x-branchid":
            branchId,

          "x-branch":
            branch,

          "x-userid":
            userId,
        }),
        [
          finYearId,
          finYear,
          branchId,
          branch,
          userId,
        ]
      );

    /* =======================================================
       DESIGN CLASSES
    ======================================================= */

    const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

    const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }`;

    const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${
            currentSkin?.glass ||
            ""
          } border-gray-200`
    }`;

    const tableRowClass = `transition ${
      isDark
        ? "bg-gray-800/20 hover:bg-gray-700/30"
        : "bg-white hover:bg-gray-50"
    }`;

    const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
      isDark
        ? "text-gray-300"
        : "text-gray-900"
    }`;

    const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
      isDark
        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
        : "border-slate-300 text-slate-700 hover:bg-slate-100"
    }`;

    /* =======================================================
       STATE
    ======================================================= */

    const [
      suppliers,
      setSuppliers,
    ] = useState([]);

    const [
      selectedSuppliers,
      setSelectedSuppliers,
    ] = useState([]);

    const [
      page,
      setPage,
    ] = useState(1);

    const [
      pageSize,
    ] = useState(10);

    const [
      totalCount,
      setTotalCount,
    ] = useState(0);

    const [
      totalPages,
      setTotalPages,
    ] = useState(0);

    const [
      supplierCode,
      setSupplierCode,
    ] = useState("");

    const [
      supplierName,
      setSupplierName,
    ] = useState("");

    const [
      loading,
      setLoading,
    ] = useState(false);

    const [
      isSearching,
      setIsSearching,
    ] = useState(false);

    const [
      showFilters,
      setShowFilters,
    ] = useState(true);

    const [
      showDeleteConfirm,
      setShowDeleteConfirm,
    ] = useState(false);

    /* =======================================================
       FETCH SUPPLIERS
    ======================================================= */

    const fetchSuppliers =
      async () => {
        setLoading(true);

        try {
          const res =
            await axios.get(
              `${API_BASE}/supplierMaster_list`,
              {
                params: {
                  page,
                  pageSize,
                },

                headers:
                  commonHeaders,
              }
            );

          setSuppliers(
            res.data
              ?.supplierList ||
              []
          );

          setTotalCount(
            res.data
              ?.totalRecords ||
              0
          );

          setTotalPages(
            res.data
              ?.totalPages ||
              0
          );
        } catch (error) {
          console.error(
            "Supplier list fetch failed:",
            error
          );

          toast.error(
            "Failed to fetch supplier list"
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    /* =======================================================
       SEARCH SUPPLIERS
    ======================================================= */

    const searchSuppliers =
      async () => {
        if (
          !toStr(
            supplierCode
          ) &&
          !toStr(
            supplierName
          )
        ) {
          toast.warning(
            "Enter Supplier Code or Name to search"
          );

          return;
        }

        setLoading(
          true
        );

        setIsSearching(
          true
        );

        try {
          const res =
            await axios.get(
              `${API_BASE}/supplierMaster_search`,
              {
                params: {
                  suppliercode:
                    supplierCode,

                  suppliername:
                    supplierName,
                },

                headers:
                  commonHeaders,
              }
            );

          const list =
            res.data
              ?.supplierListSearch ||
            [];

          setSuppliers(
            list
          );

          setTotalCount(
            list.length
          );

          setTotalPages(
            1
          );

          setPage(
            1
          );

          toast.success(
            `Found ${list.length} supplier(s)`
          );
        } catch (error) {
          console.error(
            "Supplier search failed:",
            error
          );

          toast.error(
            "Failed to search suppliers"
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    /* =======================================================
       CLEAR SEARCH
    ======================================================= */

    const clearSearch =
      () => {
        setSupplierCode(
          ""
        );

        setSupplierName(
          ""
        );

        setPage(
          1
        );

        setIsSearching(
          false
        );

        setSelectedSuppliers(
          []
        );

        fetchSuppliers();
      };

    /* =======================================================
       DELETE
    ======================================================= */

    const handleDeleteClick =
      () => {
        if (
          selectedSuppliers.length ===
          0
        ) {
          return;
        }

        setShowDeleteConfirm(
          true
        );
      };

  const confirmDeleteSelected = async () => {
    setShowDeleteConfirm(false);

    try {
      const res = await axios.post(
        "https://nissi-india.com/api/supplierMain_delete",
        {
          // FIX: previous call posted to a nonexistent, unrelated endpoint
          // (/supplierGroups_delete/delete) with groupNames — copy-pasted
          // from a different feature. Use the real supplier PK instead.
          suppliermainids: selectedSuppliers.map((s) => s.fld_suppliermainid),
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data.success) {
        toast.success("✅ Suppliers deleted successfully");
        setSelectedSuppliers([]);
        isSearching ? searchSuppliers() : fetchSuppliers();
      } else {
        toast.error(`❌ Failed to delete: ${res.data.message || "Unknown error"}`);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "❌ Error deleting suppliers"
      );
    }
  };

    /* =======================================================
       CHECKBOX
    ======================================================= */

    const handleCheckboxChange =
      (
        supplier
      ) => {
        setSelectedSuppliers(
          (
            previous
          ) => {
            const exists =
              previous.find(
                (
                  selected
                ) =>
                  selected.fld_suppliercode ===
                  supplier.fld_suppliercode
              );

            if (exists) {
              return previous.filter(
                (
                  selected
                ) =>
                  selected.fld_suppliercode !==
                  supplier.fld_suppliercode
              );
            }

            return [
              ...previous,
              supplier,
            ];
          }
        );
      };

    /* =======================================================
       SELECT ALL CURRENT PAGE
    ======================================================= */

    const allCurrentSelected =
      suppliers.length >
        0 &&
      suppliers.every(
        (
          supplier
        ) =>
          selectedSuppliers.some(
            (
              selected
            ) =>
              selected.fld_suppliercode ===
              supplier.fld_suppliercode
          )
      );

    const toggleSelectAll =
      () => {
        if (
          allCurrentSelected
        ) {
          setSelectedSuppliers(
            (
              previous
            ) =>
              previous.filter(
                (
                  selected
                ) =>
                  !suppliers.some(
                    (
                      supplier
                    ) =>
                      supplier.fld_suppliercode ===
                      selected.fld_suppliercode
                  )
              )
          );

          return;
        }

        setSelectedSuppliers(
          (
            previous
          ) => {
            const map =
              new Map();

            previous.forEach(
              (
                supplier
              ) =>
                map.set(
                  supplier.fld_suppliercode,
                  supplier
                )
            );

            suppliers.forEach(
              (
                supplier
              ) =>
                map.set(
                  supplier.fld_suppliercode,
                  supplier
                )
            );

            return Array.from(
              map.values()
            );
          }
        );
      };

    /* =======================================================
       INITIAL / PAGE LOAD
    ======================================================= */

    useEffect(() => {
      if (
        !isSearching
      ) {
        fetchSuppliers();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    /* =======================================================
       PAGE BUTTONS
    ======================================================= */

    const paginationPages =
      useMemo(() => {
        if (
          totalPages <= 5
        ) {
          return Array.from(
            {
              length:
                totalPages,
            },
            (
              _,
              index
            ) =>
              index + 1
          );
        }

        if (
          page <= 3
        ) {
          return [
            1,
            2,
            3,
            4,
            5,
          ];
        }

        if (
          page >=
          totalPages -
            2
        ) {
          return [
            totalPages -
              4,
            totalPages -
              3,
            totalPages -
              2,
            totalPages -
              1,
            totalPages,
          ];
        }

        return [
          page - 2,
          page - 1,
          page,
          page + 1,
          page + 2,
        ];
      }, [
        page,
        totalPages,
      ]);

    const showingFrom =
      totalCount ===
      0
        ? 0
        : (page - 1) *
            pageSize +
          1;

    const showingTo =
      Math.min(
        page *
          pageSize,
        totalCount
      );

    /* =======================================================
       RENDER
    ======================================================= */

    return (
      <LayoutWrapper>
        {/* ===================================================
            HEADER
        =================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              SUPPLIER LISTING
            </span>
          }
          setSkin={
            setSkin
          }
          currentSkin={
            skin
          }
        />

        <div className="space-y-6 mt-6">

          {/* =================================================
              PAGE HEADER CARD
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            <div
              className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              {/* LEFT */}

              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaUserTie className="w-5 h-5 text-white" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Supplier Master
                      Listing
                    </h3>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {
                        totalCount
                      }{" "}
                      suppliers
                    </span>

                    {selectedSuppliers.length >
                      0 && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-emerald-900/40 text-emerald-300"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {
                          selectedSuppliers.length
                        }{" "}
                        selected
                      </span>
                    )}

                    {loading && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        Loading...
                      </span>
                    )}
                  </div>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Search, add,
                    edit and manage
                    supplier master
                    records
                  </p>
                </div>
              </div>

              {/* RIGHT */}

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setShowFilters(
                      (
                        previous
                      ) =>
                        !previous
                    )
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaFilter />

                  {showFilters
                    ? "Hide Search"
                    : "Show Search"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(-1)
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaArrowLeft />

                  Back
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/inventory/Supplier-MainMas"
                    )
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <FaPlus />

                  Add New
                </button>
              </div>
            </div>
          </div>

          {/* =================================================
              SEARCH CARD
          ================================================= */}

          {showFilters && (
            <div
              className={
                cardClass
              }
            >
              {/* SEARCH HEADER */}

              <div
                className={`px-6 py-4 border-b ${
                  isDark
                    ? "border-slate-700/50 bg-slate-900/40"
                    : "border-slate-200/50 bg-white/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                  >
                    <FaSearch className="w-4 h-4 text-white" />
                  </div>

                  <div>
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Supplier Search
                    </h3>

                    <p
                      className={`text-xs ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-600"
                      }`}
                    >
                      Search by
                      Supplier Code or
                      Supplier Name
                    </p>
                  </div>
                </div>
              </div>

              {/* SEARCH BODY */}

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                  {/* CODE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Supplier Code
                    </label>

                    <input
                      type="text"
                      placeholder="Enter Supplier Code"
                      value={
                        supplierCode
                      }
                      onChange={(
                        e
                      ) =>
                        setSupplierCode(
                          e.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                      onKeyDown={(
                        e
                      ) => {
                        if (
                          e.key ===
                          "Enter"
                        ) {
                          searchSuppliers();
                        }
                      }}
                    />
                  </div>

                  {/* NAME */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Supplier Name
                    </label>

                    <input
                      type="text"
                      placeholder="Enter Supplier Name"
                      value={
                        supplierName
                      }
                      onChange={(
                        e
                      ) =>
                        setSupplierName(
                          e.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                      onKeyDown={(
                        e
                      ) => {
                        if (
                          e.key ===
                          "Enter"
                        ) {
                          searchSuppliers();
                        }
                      }}
                    />
                  </div>
                </div>

                {/* ACTION AREA */}

                <div
                  className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                    isDark
                      ? "border-slate-700"
                      : "border-slate-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={
                      clearSearch
                    }
                    className={
                      neutralButtonClass
                    }
                  >
                    <FaTimes />

                    Reset Search
                  </button>

                  <button
                    type="button"
                    onClick={
                      searchSuppliers
                    }
                    disabled={
                      loading
                    }
                    className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <FaCog className="animate-spin" />
                    ) : (
                      <FaSearch />
                    )}

                    {loading
                      ? "Searching..."
                      : "Search Suppliers"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              TABLE CARD
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            {/* TABLE HEADER */}

            <div
              className={`p-6 border-b ${
                isDark
                  ? "border-gray-700"
                  : "border-gray-200"
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                  />

                  <div>
                    <h3
                      className={`text-xl font-bold ${
                        isDark
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      Supplier List
                    </h3>

                    <p
                      className={`text-xs mt-1 ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Supplier code,
                      location and
                      contact details
                    </p>
                  </div>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {
                      totalCount
                    }{" "}
                    records
                  </span>
                </div>

                {/* SELECT / REMOVE */}

                <div className="flex flex-wrap items-center gap-3">
                  {suppliers.length >
                    0 && (
                    <button
                      type="button"
                      onClick={
                        toggleSelectAll
                      }
                      className={
                        neutralButtonClass
                      }
                    >
                      <FaCheckSquare />

                      {allCurrentSelected
                        ? "Unselect Page"
                        : "Select Page"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={
                      handleDeleteClick
                    }
                    disabled={
                      selectedSuppliers.length ===
                      0
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white shadow transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FaTrashAlt />

                    Remove Selected

                    {selectedSuppliers.length >
                      0 && (
                      <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">
                        {
                          selectedSuppliers.length
                        }
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* LOADING */}

            {loading ? (
              <div className="p-6">
                <Skeleton
                  count={6}
                  height={42}
                  className="mb-2"
                  baseColor={
                    isDark
                      ? "#374151"
                      : undefined
                  }
                  highlightColor={
                    isDark
                      ? "#4b5563"
                      : undefined
                  }
                />
              </div>
            ) : suppliers.length ===
              0 ? (
              /* EMPTY */

              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark
                      ? "bg-gray-800"
                      : "bg-gray-100"
                  }`}
                >
                  <FaUsers className="w-8 h-8 text-gray-400" />
                </div>

                <h3
                  className={`font-semibold ${
                    isDark
                      ? "text-gray-200"
                      : "text-gray-800"
                  }`}
                >
                  No Suppliers
                  Found
                </h3>

                <p
                  className={`text-sm mt-1 ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Search using the
                  filters above or add
                  a new supplier.
                </p>
              </div>
            ) : (
              <>
                {/* TABLE */}

                <div className="overflow-x-auto">
                  <table className="min-w-[1100px] w-full text-sm">
                    <thead
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                    >
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          S.No
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Code
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Name
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          State
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Zip
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Mobile
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Select
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Edit
                        </th>
                      </tr>
                    </thead>

                    <tbody
                      className={`divide-y ${
                        isDark
                          ? "divide-gray-700"
                          : "divide-gray-200"
                      }`}
                    >
                      {suppliers.map(
                        (
                          supplier,
                          index
                        ) => {
                          const isSelected =
                            selectedSuppliers.some(
                              (
                                selected
                              ) =>
                                selected.fld_suppliercode ===
                                supplier.fld_suppliercode
                            );

                          return (
                            <tr
                              key={
                                supplier.fld_suppliercode
                              }
                              className={`${tableRowClass} ${
                                isSelected
                                  ? isDark
                                    ? `bg-${baseColor}-900/20`
                                    : `bg-${baseColor}-50/70`
                                  : ""
                              }`}
                            >
                              {/* S.NO */}

                              <td
                                className={`${tableCellClass} text-center font-medium`}
                              >
                                {(page -
                                  1) *
                                  pageSize +
                                  index +
                                  1}
                              </td>

                              {/* CODE */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                <span
                                  className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold ${
                                    isDark
                                      ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                      : `bg-${baseColor}-50 text-${baseColor}-700`
                                  }`}
                                >
                                  {supplier.fld_suppliercode ||
                                    "-"}
                                </span>
                              </td>

                              {/* NAME */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <FaUserTie
                                    className={`text-${baseColor}-500 flex-shrink-0`}
                                  />

                                  <span className="font-semibold">
                                    {supplier.fld_suppliername ||
                                      "-"}
                                  </span>
                                </div>
                              </td>

                              {/* STATE */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                {supplier.fld_statename ||
                                  "-"}
                              </td>

                              {/* ZIP */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                {supplier.fld_ZipCode ||
                                  "-"}
                              </td>

                              {/* MOBILE */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                {supplier.fld_Mobile ||
                                  "-"}
                              </td>

                              {/* CHECKBOX */}

                              <td className="px-4 py-3 text-center">
                                <label className="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={
                                      isSelected
                                    }
                                    onChange={() =>
                                      handleCheckboxChange(
                                        supplier
                                      )
                                    }
                                    className={`w-5 h-5 rounded border-gray-300 accent-${baseColor}-600 cursor-pointer`}
                                  />
                                </label>
                              </td>

                              {/* EDIT */}

                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      "/inventory/Supplier-Main-MasEdit",
                                      {
                                        state: {
                                          supplier,
                                        },
                                      }
                                    )
                                  }
                                  className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                                  title="Edit Supplier"
                                >
                                  <FaEdit
                                    size={
                                      14
                                    }
                                  />
                                </button>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>

                {/* =============================================
                    PAGINATION
                ============================================= */}

                {!isSearching &&
                  totalCount >
                    0 && (
                    <div
                      className={`p-6 border-t ${
                        isDark
                          ? "border-gray-700"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">

                        {/* INFO */}

                        <div
                          className={`text-sm ${
                            isDark
                              ? "text-gray-300"
                              : "text-gray-700"
                          }`}
                        >
                          Showing{" "}
                          {
                            showingFrom
                          }{" "}
                          to{" "}
                          {
                            showingTo
                          }{" "}
                          of{" "}
                          {
                            totalCount
                          }{" "}
                          results
                        </div>

                        {/* PAGE CONTROLS */}

                        <div
                          className={`inline-flex rounded-lg shadow-sm overflow-hidden border ${
                            isDark
                              ? "border-gray-700 bg-gray-900"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {/* PREVIOUS */}

                          <button
                            type="button"
                            onClick={() =>
                              setPage(
                                (
                                  previous
                                ) =>
                                  Math.max(
                                    previous -
                                      1,
                                    1
                                  )
                              )
                            }
                            disabled={
                              page ===
                              1
                            }
                            className={`px-4 py-2 text-sm font-medium transition ${
                              page ===
                              1
                                ? "text-gray-400 cursor-not-allowed"
                                : isDark
                                ? "text-gray-200 hover:bg-gray-800"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Previous
                          </button>

                          {/* PAGE NUMBERS */}

                          {paginationPages.map(
                            (
                              pageNumber
                            ) => (
                              <button
                                type="button"
                                key={
                                  pageNumber
                                }
                                onClick={() =>
                                  setPage(
                                    pageNumber
                                  )
                                }
                                className={`px-4 py-2 text-sm font-medium transition ${
                                  page ===
                                  pageNumber
                                    ? `bg-${baseColor}-${baseShade} text-white`
                                    : isDark
                                    ? "text-gray-200 hover:bg-gray-800"
                                    : "text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                {
                                  pageNumber
                                }
                              </button>
                            )
                          )}

                          {/* NEXT */}

                          <button
                            type="button"
                            onClick={() =>
                              setPage(
                                (
                                  previous
                                ) =>
                                  Math.min(
                                    previous +
                                      1,
                                    totalPages
                                  )
                              )
                            }
                            disabled={
                              page ===
                              totalPages
                            }
                            className={`px-4 py-2 text-sm font-medium transition ${
                              page ===
                              totalPages
                                ? "text-gray-400 cursor-not-allowed"
                                : isDark
                                ? "text-gray-200 hover:bg-gray-800"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                {/* SEARCH RESULT FOOTER */}

                {isSearching &&
                  suppliers.length >
                    0 && (
                    <div
                      className={`px-6 py-4 border-t ${
                        isDark
                          ? "border-gray-700 bg-gray-900/30"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div
                        className={`text-sm ${
                          isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        Search result:{" "}
                        <span className="font-semibold">
                          {
                            suppliers.length
                          }
                        </span>{" "}
                        supplier(s)
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>

        {/* ===================================================
            TOAST
        =================================================== */}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={
            isDark
              ? "dark"
              : "light"
          }
        />

        {/* ===================================================
            DELETE CONFIRM MODAL
        =================================================== */}

        <ConfirmModal
          open={
            showDeleteConfirm
          }
          title="Delete Suppliers"
          message={`Are you sure you want to delete ${selectedSuppliers.length} supplier(s)? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={
            confirmDeleteSelected
          }
          onCancel={() =>
            setShowDeleteConfirm(
              false
            )
          }
        />
      </LayoutWrapper>
    );
  };

export default SupplierMainMasListing;